const ProductDescriptionToggle = (() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const OVERFLOW_THRESHOLD = 8;

  const getCollapsedHeight = (root) => {
    const raw = getComputedStyle(root).getPropertyValue('--product-description-collapsed-height').trim();
    if (!raw) return 120;

    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return 120;

    if (raw.endsWith('rem')) {
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return value * rootFontSize;
    }

    if (raw.endsWith('em')) {
      const fontSize = parseFloat(getComputedStyle(root).fontSize) || 16;
      return value * fontSize;
    }

    return value;
  };

  const setLabel = (toggle, expanded) => {
    const label = toggle.querySelector('[data-product-description-label]');
    if (!label) return;
    label.textContent = expanded
      ? (theme.strings.seeLess || 'Show less')
      : (theme.strings.seeMore || 'Show more');
    toggle.setAttribute('aria-expanded', String(expanded));
  };

  const finishOpen = (root, clip) => {
    clip.style.transition = 'none';
    clip.style.height = 'auto';
    clip.offsetHeight;
    root.classList.add('is-expanded');
    root.classList.remove('is-opening');
    requestAnimationFrame(() => {
      clip.style.transition = '';
    });
  };

  const finishClose = (root, clip, collapsedHeight) => {
    clip.style.transition = 'none';
    clip.style.height = `${collapsedHeight}px`;
    clip.offsetHeight;
    root.classList.remove('is-closing');
    requestAnimationFrame(() => {
      clip.style.transition = '';
    });
  };

  const open = (root, clip, inner, toggle) => {
    const collapsedHeight = getCollapsedHeight(root);
    const endHeight = inner.scrollHeight;

    root.classList.add('is-opening');
    clip.style.height = `${collapsedHeight}px`;
    clip.style.overflow = 'hidden';
    setLabel(toggle, true);

    if (prefersReducedMotion) {
      finishOpen(root, clip);
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clip.style.height = `${endHeight}px`;
      });
    });

    const onTransitionEnd = (event) => {
      if (event.target !== clip || event.propertyName !== 'height') return;
      clip.removeEventListener('transitionend', onTransitionEnd);
      finishOpen(root, clip);
    };

    clip.addEventListener('transitionend', onTransitionEnd);
  };

  const close = (root, clip, toggle) => {
    const collapsedHeight = getCollapsedHeight(root);
    const startHeight = clip.scrollHeight;

    root.classList.add('is-closing');
    root.classList.remove('is-expanded');
    clip.style.height = `${startHeight}px`;
    clip.style.overflow = 'hidden';
    setLabel(toggle, false);

    if (prefersReducedMotion) {
      finishClose(root, clip, collapsedHeight);
      return;
    }

    requestAnimationFrame(() => {
      clip.style.height = `${collapsedHeight}px`;
    });

    const onTransitionEnd = (event) => {
      if (event.target !== clip || event.propertyName !== 'height') return;
      clip.removeEventListener('transitionend', onTransitionEnd);
      finishClose(root, clip, collapsedHeight);
    };

    clip.addEventListener('transitionend', onTransitionEnd);
  };

  const setup = (root) => {
    if (root.getAttribute('data-product-description-init') === 'true') return;

    const clip = root.querySelector('[data-product-description-clip]');
    const inner = root.querySelector('[data-product-description-inner]');
    const toggle = root.querySelector('[data-product-description-toggle]');

    if (!clip || !inner || !toggle) return;

    root.setAttribute('data-product-description-init', 'true');

    const collapsedHeight = getCollapsedHeight(root);
    const needsToggle = inner.scrollHeight > collapsedHeight + OVERFLOW_THRESHOLD;

    if (!needsToggle) {
      toggle.hidden = true;
      return;
    }

    root.classList.add('is-collapsible');
    clip.style.height = `${collapsedHeight}px`;
    toggle.hidden = false;
    setLabel(toggle, false);

    toggle.addEventListener('click', () => {
      if (root.classList.contains('is-opening') || root.classList.contains('is-closing')) {
        return;
      }

      if (root.classList.contains('is-expanded')) {
        close(root, clip, toggle);
      } else {
        open(root, clip, inner, toggle);
      }
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-product-description]').forEach(setup);
  };

  return { init };
})();

const updatePickerLabelOnHover = (labelSelector, variantSelector) => {
  const currentLabel = document.querySelector(labelSelector);
  const variants = document.querySelectorAll(variantSelector);

  if (!currentLabel || !variants.length) return;

  const defaultLabel = currentLabel.textContent;

  variants.forEach(variant => {
    const label = variant.getAttribute("data-label");

    variant.addEventListener("mouseenter", () => {
      if (label) currentLabel.textContent = label;
    });

    variant.addEventListener("mouseleave", () => {
      currentLabel.textContent = defaultLabel;
    });
  });
};

const updateColorLabelOnHover = () => {
  updatePickerLabelOnHover("[data-current-label]", ".product-variant:not(.product-variant--size)");
};

const updateSizeLabelOnHover = () => {
  updatePickerLabelOnHover("[data-current-size-label]", ".product-variant--size");
};

const updateBundleTitleOnHover = () => {
  const currentTitle = document.querySelector("[data-current-item-title]");
  const bundleItems = document.querySelectorAll(".bundles-container__img");

  if (!currentTitle || !bundleItems.length) return;

  const defaultTitle = currentTitle.textContent;

  bundleItems.forEach(item => {
    const title = item.getAttribute("data-product-title");

    item.addEventListener("mouseenter", () => {
      if (title) currentTitle.textContent = title;
    });

    item.addEventListener("mouseleave", () => {
      currentTitle.textContent = defaultTitle;
    });
  });
};

const HighlightsSlider = (() => {
  let slider = null;

  const init = () => {
    const wrapper = document.querySelector('.highlights-grid');
    if (!wrapper) return;

    const items = wrapper.querySelectorAll('.highlights-grid__item');
    const itemCount = items.length;
    if (itemCount <= 1) return;

    const isMobile = window.innerWidth <= 768;
    const shouldInitOnDesktop = itemCount >= 5;

    // ✅ Initialize conditions
    if ((isMobile || shouldInitOnDesktop) && !slider) {
      slider = new Flickity(wrapper, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: true,
        contain: true,
      });
    } 
    // ✅ Destroy when not mobile and less than 5 items
    else if (!isMobile && itemCount < 5 && slider) {
      slider.destroy();
      slider = null;
    }
  };

  // Optional: debounce resize for performance
  const resizeHandler = () => {
    clearTimeout(window._highlightsSliderTimeout);
    window._highlightsSliderTimeout = setTimeout(() => {
      init();
    }, 150);
  };

  const setup = () => {
    init();
    window.addEventListener('resize', resizeHandler);
  };

  return { setup };
})();

const InstructionsSlider = (() => {
  let slider = null;

  const init = () => {
    const wrapper = document.querySelector('.instructions-grid');
    if (!wrapper) return;

    const items = wrapper.querySelectorAll('.instructions-grid__item');
    const itemCount = items.length;
    if (itemCount <= 1) return;

    const isMobile = window.innerWidth <= 768;
    const shouldInitOnDesktop = itemCount > 4;

    if ((isMobile || shouldInitOnDesktop) && !slider) {
      slider = new Flickity(wrapper, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: true,
        contain: true,
      });
    } else if (!isMobile && itemCount <= 4 && slider) {
      slider.destroy();
      slider = null;
    }
  };

  const resizeHandler = () => {
    clearTimeout(window._instructionsSliderTimeout);
    window._instructionsSliderTimeout = setTimeout(() => {
      init();
    }, 150);
  };

  const setup = () => {
    init();
    window.addEventListener('resize', resizeHandler);
  };

  return { setup };
})();

const WarrantyHighlightsSlider = (() => {
  let slider = null;

  const init = () => {
    const wrapper = document.querySelector('.warranty-section__highlights');
    if (!wrapper) return;

    const items = wrapper.querySelectorAll('.warranty-section__highlight');
    const itemCount = items.length;
    if (itemCount <= 1) return;

    const isMobile = window.innerWidth <= 768;

    if (isMobile && !slider) {
      slider = new Flickity(wrapper, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        groupCells: false,
        contain: true,
      });
    } else if (!isMobile && slider) {
      slider.destroy();
      slider = null;
    }
  };

  const resizeHandler = () => {
    clearTimeout(window._warrantyHighlightsSliderTimeout);
    window._warrantyHighlightsSliderTimeout = setTimeout(() => {
      init();
    }, 150);
  };

  const setup = () => {
    init();
    window.addEventListener('resize', resizeHandler);
  };

  return { setup };
})();


const ProductFaqAccordion = (() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const finishOpen = (details, content) => {
    content.style.transition = 'none';
    content.style.height = `${content.scrollHeight}px`;
    content.offsetHeight;
    content.style.removeProperty('height');
    content.style.overflow = '';
    details.classList.remove('is-opening');
    requestAnimationFrame(() => {
      content.style.transition = '';
    });
  };

  const finishClose = (details, content) => {
    content.style.transition = 'none';
    details.removeAttribute('open');
    content.style.removeProperty('height');
    content.style.overflow = '';
    details.classList.remove('is-closing');
    requestAnimationFrame(() => {
      content.style.transition = '';
    });
  };

  const openItem = (details, content) => {
    details.setAttribute('open', '');
    details.classList.add('is-opening');
    content.style.height = '0px';
    content.style.overflow = 'hidden';

    if (prefersReducedMotion) {
      finishOpen(details, content);
      return;
    }

    requestAnimationFrame(() => {
      const endHeight = content.scrollHeight;

      requestAnimationFrame(() => {
        content.style.height = `${endHeight}px`;
      });
    });

    const onTransitionEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;
      content.removeEventListener('transitionend', onTransitionEnd);
      finishOpen(details, content);
    };

    content.addEventListener('transitionend', onTransitionEnd);
  };

  const closeItem = (details, content) => {
    if (prefersReducedMotion) {
      finishClose(details, content);
      return;
    }

    details.classList.add('is-closing');
    const startHeight = content.scrollHeight;
    content.style.height = `${startHeight}px`;
    content.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      content.style.height = '0px';
    });

    const onTransitionEnd = (event) => {
      if (event.target !== content || event.propertyName !== 'height') return;
      content.removeEventListener('transitionend', onTransitionEnd);
      finishClose(details, content);
    };

    content.addEventListener('transitionend', onTransitionEnd);
  };

  const init = () => {
    document.querySelectorAll('.product-faq__item:not([data-faq-init])').forEach((details) => {
      const summary = details.querySelector('.product-faq__trigger');
      const content = details.querySelector('.product-faq__content');

      if (!summary || !content) return;

      details.setAttribute('data-faq-init', 'true');

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        if (details.classList.contains('is-opening') || details.classList.contains('is-closing')) {
          return;
        }

        if (details.hasAttribute('open')) {
          closeItem(details, content);
        } else {
          openItem(details, content);
        }
      });
    });
  };

  return { init };
})();

const UgcVideos = (() => {
  const SWIPE_THRESHOLD = 60;
  const DESKTOP_MIN = 769;
  const MODAL_ANIMATION_MS = 350;

  class UgcVideosInstance {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector('.ugc-videos__modal');
      this.sliderEl = root.querySelector('.ugc-videos__slider');
      this.cards = [...root.querySelectorAll('[data-ugc-card]')];
      this.slides = this.modal ? [...this.modal.querySelectorAll('[data-ugc-modal-slide]')] : [];
      this.productLists = this.modal ? [...this.modal.querySelectorAll('[data-ugc-products-list]')] : [];
      this.videoCount = parseInt(root.dataset.videoCount, 10) || this.slides.length;
      this.activeIndex = 0;
      this.isModalOpen = false;
      this.isModalClosing = false;
      this.isMuted = true;
      this.slider = null;
      this.productsSliders = new Map();
      this.lastFocus = null;
      this.touchStartY = 0;
      this.touchStartX = 0;
      this.chatElements = null;

      if (this.modal && this.modal.parentElement !== document.body) {
        document.body.appendChild(this.modal);
      }

      this.bindEvents();
      this.initSlider();
      this.initAjaxForms();
      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize);
    }

    bindEvents() {
      this.cards.forEach((card) => {
        const video = card.querySelector('.ugc-videos__video');
        const playBtn = card.querySelector('.ugc-videos__play-btn');
        const muteBtn = card.querySelector('.ugc-videos__mute-btn');

        card.addEventListener('click', (event) => {
          if (event.target.closest('.ugc-videos__mute-btn')) return;
          const index = parseInt(card.dataset.index, 10);
          this.openModal(index);
        });

        card.addEventListener('mouseenter', () => {
          if (!this.isDesktop() || !video) return;
          card.classList.add('is-playing');
          if (muteBtn) muteBtn.hidden = false;
          video.muted = true;
          video.play().catch(() => {});
        });

        card.addEventListener('mouseleave', () => {
          if (!video) return;
          card.classList.remove('is-playing');
          video.pause();
          video.currentTime = 0;
          video.muted = true;
          if (muteBtn) {
            muteBtn.hidden = true;
            this.updateMuteButton(muteBtn, true);
          }
        });

        if (muteBtn && video) {
          muteBtn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            video.muted = !video.muted;
            this.updateMuteButton(muteBtn, video.muted);
          });
        }
      });

      this.modal.querySelectorAll('[data-ugc-modal-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeModal());
      });

      const modalInner = this.modal.querySelector('.ugc-videos__modal-inner');
      if (modalInner) {
        modalInner.addEventListener('click', (event) => {
          if (!this.isDesktop() || !this.isModalOpen || this.isModalClosing) return;
          if (event.target.closest('.ugc-videos__modal-stage')) return;
          this.closeModal();
        });
      }

      const prevBtn = this.modal.querySelector('[data-ugc-prev]');
      const nextBtn = this.modal.querySelector('[data-ugc-next]');
      if (prevBtn) prevBtn.addEventListener('click', () => this.goTo(this.activeIndex - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.goTo(this.activeIndex + 1));

      const modalMuteBtn = this.modal.querySelector('[data-ugc-modal-mute]');
      if (modalMuteBtn) {
        modalMuteBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.isMuted = !this.isMuted;
          this.applyModalMute();
          this.updateMuteButton(modalMuteBtn, this.isMuted);
        });
      }

      const swipeArea = this.modal.querySelector('[data-ugc-swipe-area]');
      if (swipeArea) {
        swipeArea.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        swipeArea.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
        swipeArea.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      }

      this.modal.querySelectorAll('.ugc-videos__product-form').forEach((form) => {
        form.addEventListener('click', (event) => event.stopPropagation());
        form.addEventListener('submit', (event) => event.stopPropagation());
      });

      document.addEventListener('keydown', this.onKeyDown.bind(this));
    }

    onKeyDown(event) {
      if (!this.isModalOpen) return;
      if (event.key === 'Escape') this.closeModal();
      if (event.key === 'ArrowLeft') this.goTo(this.activeIndex - 1);
      if (event.key === 'ArrowRight') this.goTo(this.activeIndex + 1);
    }

    onTouchStart(event) {
      if (!this.isModalOpen || this.isDesktop()) return;
      const touch = event.touches[0];
      this.touchStartY = touch.clientY;
      this.touchStartX = touch.clientX;
    }

    onTouchMove(event) {
      if (!this.isModalOpen || this.isDesktop()) return;
      const touch = event.touches[0];
      const deltaY = Math.abs(touch.clientY - this.touchStartY);
      const deltaX = Math.abs(touch.clientX - this.touchStartX);
      if (deltaY > deltaX && deltaY > 10) {
        event.preventDefault();
      }
    }

    onTouchEnd(event) {
      if (!this.isModalOpen || this.isDesktop()) return;
      const touch = event.changedTouches[0];
      const deltaY = touch.clientY - this.touchStartY;
      const deltaX = touch.clientX - this.touchStartX;
      if (Math.abs(deltaY) < SWIPE_THRESHOLD || Math.abs(deltaY) < Math.abs(deltaX)) return;
      if (deltaY > 0) this.goTo(this.activeIndex + 1);
      else this.goTo(this.activeIndex - 1);
    }

    isDesktop() {
      return window.innerWidth >= DESKTOP_MIN;
    }

    initSlider() {
      if (!this.sliderEl || this.videoCount <= 1) {
        this.destroySlider();
        return;
      }

      if (this.isDesktop()) {
        this.destroySlider();
        return;
      }

      if (this.slider) return;

      this.slider = new Flickity(this.sliderEl, {
        adaptiveHeight: false,
        initialIndex: 0,
        pageDots: true,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        contain: true,
      });
    }

    destroySlider() {
      if (!this.slider) return;
      this.slider.destroy();
      this.slider = null;
    }

    onResize() {
      this.initSlider();
    }

    updateMuteButton(button, isMuted) {
      button.setAttribute('aria-pressed', String(isMuted));
      button.setAttribute(
        'aria-label',
        isMuted ? 'Unmute video' : 'Mute video'
      );
      const mutedIcon = button.querySelector('.ugc-videos__mute-icon--muted');
      const unmutedIcon = button.querySelector('.ugc-videos__mute-icon--unmuted');
      if (mutedIcon) mutedIcon.hidden = !isMuted;
      if (unmutedIcon) unmutedIcon.hidden = isMuted;
    }

    suppressShopifyChat() {
      const elements = [
        document.getElementById('ShopifyChat'),
        ...document.querySelectorAll('inbox-online-store-chat'),
      ].filter(Boolean);

      this.chatElements = elements;
      elements.forEach((el) => {
        if (el.dataset.ugcChatZIndex === undefined) {
          el.dataset.ugcChatZIndex = el.style.zIndex || '';
        }
        el.style.setProperty('z-index', '2147483646', 'important');
      });
    }

    restoreShopifyChat() {
      (this.chatElements || []).forEach((el) => {
        if (el.dataset.ugcChatZIndex !== undefined) {
          if (el.dataset.ugcChatZIndex) {
            el.style.zIndex = el.dataset.ugcChatZIndex;
          } else {
            el.style.removeProperty('z-index');
          }
          delete el.dataset.ugcChatZIndex;
        }
      });
      this.chatElements = null;
    }

    openModal(index) {
      if (!this.modal || this.isModalOpen || this.isModalClosing) return;
      this.lastFocus = document.activeElement;
      this.isModalOpen = true;
      this.activeIndex = index;
      this.modal.hidden = false;
      this.modal.classList.remove('is-open');
      document.body.classList.add('ugc-videos-modal-open');
      this.suppressShopifyChat();
      this.showSlide(index);
      this.initAjaxForms();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modal.classList.add('is-open');
        });
      });

      const closeBtn = this.modal.querySelector(
        this.isDesktop() ? '.ugc-videos__modal-close--desktop' : '.ugc-videos__modal-close--mobile'
      );
      if (closeBtn) closeBtn.focus();
    }

    finishCloseModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.modal.classList.remove('is-open');
      this.isModalClosing = false;
      document.body.classList.remove('ugc-videos-modal-open');
      this.restoreShopifyChat();
      this.pauseAllModalVideos();
      this.destroyMobileProductsSliders();
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    closeModal() {
      if (!this.modal || !this.isModalOpen || this.isModalClosing) return;
      this.isModalOpen = false;
      this.isModalClosing = true;
      this.modal.classList.remove('is-open');

      const inner = this.modal.querySelector('.ugc-videos__modal-inner');
      let didFinish = false;
      const finish = () => {
        if (didFinish) return;
        didFinish = true;
        if (inner) inner.removeEventListener('transitionend', onTransitionEnd);
        this.finishCloseModal();
      };

      const onTransitionEnd = (event) => {
        if (event.target !== inner || event.propertyName !== 'transform') return;
        finish();
      };

      if (inner) {
        inner.addEventListener('transitionend', onTransitionEnd);
      }

      window.setTimeout(finish, MODAL_ANIMATION_MS + 50);
    }

    initAjaxForms() {
      if (typeof theme === 'undefined' || !theme.AjaxProduct) return;

      this.modal.querySelectorAll('.ugc-videos__product-form:not([data-ajax-init])').forEach((form) => {
        new theme.AjaxProduct(form, '[data-add-to-cart]');
        form.setAttribute('data-ajax-init', 'true');
      });
    }

    pauseAllModalVideos() {
      this.slides.forEach((slide) => {
        const video = slide.querySelector('.ugc-videos__video--modal');
        if (!video) return;
        video.pause();
        video.currentTime = 0;
        video.onended = null;
      });
    }

    getWrappedIndex(index) {
      if (this.videoCount <= 0) return 0;
      return (index + this.videoCount) % this.videoCount;
    }

    goTo(index) {
      const nextIndex = this.getWrappedIndex(index);
      if (nextIndex === this.activeIndex) {
        if (this.videoCount === 1) {
          this.replayActiveVideo();
        }
        return;
      }
      this.activeIndex = nextIndex;
      this.showSlide(nextIndex);
    }

    replayActiveVideo() {
      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide) return;
      const video = activeSlide.querySelector('.ugc-videos__video--modal');
      if (!video) return;
      video.currentTime = 0;
      video.muted = this.isMuted;
      video.play().catch(() => {});
    }

    showSlide(index) {
      this.slides.forEach((slide, i) => {
        const isActive = i === index;
        slide.classList.toggle('is-active', isActive);
        slide.hidden = !isActive;
        const video = slide.querySelector('.ugc-videos__video--modal');
        if (video) {
          video.pause();
          video.currentTime = 0;
          video.onended = null;
          if (!isActive) return;
          video.muted = this.isMuted;
          video.onended = () => this.goTo(index + 1);
          video.play().catch(() => {});
        }
      });

      this.productLists.forEach((list, i) => {
        const isActive = i === index;
        list.classList.toggle('is-active', isActive);
        list.hidden = !isActive;
      });

      this.initMobileProductsSlider(index);
      this.applyModalMute();
    }

    applyModalMute() {
      const modalMuteBtn = this.modal.querySelector('[data-ugc-modal-mute]');
      if (modalMuteBtn) this.updateMuteButton(modalMuteBtn, this.isMuted);

      const activeSlide = this.slides[this.activeIndex];
      if (!activeSlide) return;
      const video = activeSlide.querySelector('.ugc-videos__video--modal');
      if (video) video.muted = this.isMuted;
    }

    destroyMobileProductsSliders() {
      this.productsSliders.forEach((slider) => {
        slider.element.querySelectorAll('.ugc-videos__modal-products-cell:not(.ugc-videos__modal-products-cell--gutter)').forEach((cell) => {
          cell.style.width = '';
          cell.style.marginLeft = '';
          cell.style.marginRight = '';
        });
        slider.element.querySelectorAll('.ugc-videos__modal-products-cell--gutter').forEach((gutter) => gutter.remove());
        slider.destroy();
      });
      this.productsSliders.clear();
    }

    initMobileProductsSlider(index) {
      if (this.isDesktop()) return;

      this.destroyMobileProductsSliders();

      const slide = this.slides[index];
      if (!slide) return;
      const sliderEl = slide.querySelector('[data-ugc-products-slider]');
      if (!sliderEl) return;

      const productCells = [...sliderEl.querySelectorAll(':scope > .ugc-videos__modal-products-cell:not(.ugc-videos__modal-products-cell--gutter)')];
      if (productCells.length <= 1) return;

      const gap = 12;
      const leadingGutter = document.createElement('div');
      leadingGutter.className = 'ugc-videos__modal-products-cell ugc-videos__modal-products-cell--gutter';
      leadingGutter.setAttribute('aria-hidden', 'true');

      const trailingGutter = document.createElement('div');
      trailingGutter.className = 'ugc-videos__modal-products-cell ugc-videos__modal-products-cell--gutter';
      trailingGutter.setAttribute('aria-hidden', 'true');

      sliderEl.insertBefore(leadingGutter, sliderEl.firstChild);
      sliderEl.appendChild(trailingGutter);

      productCells.forEach((cell) => {
        cell.style.width = '85%';
        cell.style.marginRight = `${gap}px`;
        cell.style.marginLeft = '';
      });

      const slider = new Flickity(sliderEl, {
        adaptiveHeight: false,
        pageDots: false,
        wrapAround: false,
        cellAlign: 'left',
        prevNextButtons: false,
        contain: true,
        initialIndex: 0,
      });

      this.productsSliders.set(index, slider);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('ugc-videos:not([data-ugc-init])').forEach((root) => {
      root.setAttribute('data-ugc-init', 'true');
      new UgcVideosInstance(root);
    });
  };

  return { init };
})();


document.addEventListener("DOMContentLoaded", () => {
  ProductDescriptionToggle.init();
  updateColorLabelOnHover();
  updateSizeLabelOnHover();
  updateBundleTitleOnHover();
  HighlightsSlider.setup();
  InstructionsSlider.setup();
  WarrantyHighlightsSlider.setup();
  ProductFaqAccordion.init();
  UgcVideos.init();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('[data-product-description]')) {
    ProductDescriptionToggle.init(event.target);
  }
  if (event.target.querySelector('.product-faq__list')) {
    ProductFaqAccordion.init();
  }
  if (event.target.querySelector('ugc-videos')) {
    UgcVideos.init(event.target);
  }
  if (event.target.querySelector('.warranty-section__highlights')) {
    WarrantyHighlightsSlider.setup();
  }
});